import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { AlertBanner } from "@/components/admin/alert-banner";
import { AdminBarChart } from "@/components/admin/bar-chart";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminDonut } from "@/components/admin/donut";
import { Kpi } from "@/components/admin/kpi";
import { AdminLineChart } from "@/components/admin/line-chart";
import { PageHead } from "@/components/admin/page-head";
import { Segmented } from "@/components/admin/segmented";
import {
	COUNTRY_LABEL,
	DEVICE_LABEL,
	DimensionBars,
	INAPP_LABEL,
	REFERRER_LABEL,
	flagOf,
} from "@/components/admin/short-links/dimension-bars";
import { usePageTitle } from "@/hooks/use-page-title";
import { fmtN } from "@/lib/format";
import { CHANNEL_LABEL } from "@/lib/short-links/short-links-api";
import { useShortLinksOverview } from "@/lib/short-links/short-links-hooks";

const PERIODS = [
	{ value: "7", label: "7 jours" },
	{ value: "30", label: "30 jours" },
	{ value: "90", label: "90 jours" },
];

const DONUT_COLORS = [
	"var(--a-terracotta)",
	"var(--a-forest)",
	"var(--a-ochre)",
	"var(--a-info)",
	"var(--a-success)",
	"var(--a-ink-mute)",
];

export function AdminShortLinksStatsPage() {
	usePageTitle("Statistiques des liens courts");
	const navigate = useNavigate();
	const [days, setDays] = useState("30");

	const { data, isLoading } = useShortLinksOverview(Number(days));
	const series = data?.series ?? [];
	const dims = data?.dimensions ?? {};
	const totals = data?.totals;

	const dayLabels = series.map((s) => s.date.slice(5).replace("-", "/"));
	const humans = series.map((s) => s.humans);
	const hourRows = dims.hour ?? [];

	const channelSlices = (dims.channel ?? []).slice(0, 6).map((row, i) => ({
		color: DONUT_COLORS[i % DONUT_COLORS.length],
		value: row.clicks,
	}));
	const channelTotal = channelSlices.reduce((sum, s) => sum + s.value, 0);

	return (
		<>
			<PageHead
				title="Statistiques des liens courts"
				sub="Ouvertures mesurées sur akwa.ba — navigateur, appareil, pays, provenance"
				actions={<Segmented options={PERIODS} value={days} onChange={setDays} />}
			/>

			{/* Bandeau d'honnêteté : mieux vaut expliquer un tableau vide que le
			    laisser croire à une absence de trafic. */}
			{totals && totals.geoUnknownRate > 0.5 ? (
				<AlertBanner
					variant="info"
					title={`La géolocalisation n'est pas disponible pour ${Math.round(totals.geoUnknownRate * 100)} % des clics de cette période`}
					description="Le pays est fourni par le réseau de diffusion. Il est absent quand le trafic ne passe pas par lui — par exemple en développement, ou si le domaine court a été remis en mode direct."
				/>
			) : null}

			<div className="kpi-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
				<Kpi
					label="Clics humains"
					value={fmtN(totals?.humans ?? 0)}
					variant="accent"
					icon="trend"
					spark={humans}
				/>
				<Kpi label="Visiteurs uniques" value={fmtN(totals?.uniques ?? 0)} icon="users" />
				<Kpi
					label="Robots et pré-visites"
					value={`${Math.round((totals?.nonHumanRate ?? 0) * 100)} %`}
					variant="warning"
					icon="shield"
					foot="aperçus de partage et passerelles opérateur"
				/>
				<Kpi label="Liens actifs" value={fmtN(data?.activeLinks ?? 0)} icon="zap" variant="forest" />
			</div>

			<div className="stack" style={{ marginTop: 16 }}>
				<Card>
					<CardHead
						title="Clics par jour"
						sub="Clics humains uniquement. Les journées sans clic sont affichées à zéro."
					/>
					<CardBody>
						{isLoading ? (
							<div className="muted center" style={{ padding: 32 }}>
								Chargement…
							</div>
						) : (
							<AdminLineChart
								data={humans}
								labels={dayLabels}
								color="var(--a-terracotta)"
								height={240}
							/>
						)}
					</CardBody>
				</Card>

				<div className="grid-3">
					<Card>
						<CardHead title="Navigateur" />
						<CardBody>
							<DimensionBars rows={dims.browser ?? []} />
						</CardBody>
					</Card>
					<Card>
						<CardHead title="Système" />
						<CardBody>
							<DimensionBars rows={dims.os ?? []} />
						</CardBody>
					</Card>
					<Card>
						<CardHead title="Appareil" />
						<CardBody>
							<DimensionBars rows={dims.device ?? []} labels={DEVICE_LABEL} />
						</CardBody>
					</Card>
				</div>

				<div className="grid-2">
					<Card>
						<CardHead title="Pays" />
						<CardBody>
							<DimensionBars
								rows={dims.country ?? []}
								labels={COUNTRY_LABEL}
								prefix={(v) => flagOf(v)}
							/>
							<div className="muted" style={{ fontSize: 12, marginTop: 12 }}>
								Pays déduit de l'adresse réseau. La ville n'est pas mesurée : les
								connexions mobiles camerounaises sortent par quelques passerelles à
								Douala et Yaoundé, un découpage par ville n'aurait aucun sens.
							</div>
						</CardBody>
					</Card>

					<Card>
						<CardHead title="Canal" sub="Déclaré à la création du lien" />
						<CardBody>
							{channelTotal === 0 ? (
								<div className="muted center" style={{ padding: 24 }}>
									Aucune donnée sur cette période.
								</div>
							) : (
								<div style={{ display: "flex", gap: 20, alignItems: "center" }}>
									<AdminDonut slices={channelSlices} total={channelTotal} size={130} />
									<div style={{ flex: 1 }}>
										<DimensionBars rows={dims.channel ?? []} labels={CHANNEL_LABEL} max={6} />
									</div>
								</div>
							)}
						</CardBody>
					</Card>
				</div>

				<div className="grid-2">
					<Card>
						<CardHead
							title="Provenance"
							sub="Application depuis laquelle le lien a été ouvert"
						/>
						<CardBody>
							<DimensionBars rows={dims.inapp ?? []} labels={INAPP_LABEL} />
							<div className="muted" style={{ fontSize: 12, marginTop: 12 }}>
								C'est la seule provenance mesurable sur un lien envoyé par SMS : le
								référent y est presque toujours absent.
							</div>
						</CardBody>
					</Card>

					<Card>
						<CardHead title="Référent" />
						<CardBody>
							<DimensionBars rows={dims.referrer ?? []} labels={REFERRER_LABEL} />
						</CardBody>
					</Card>
				</div>

				<Card>
					<CardHead
						title="Heure d'ouverture"
						sub="Heure de Douala. Un pic dans les minutes qui suivent un envoi trahit les pré-visites d'opérateur."
					/>
					<CardBody>
						{hourRows.length === 0 ? (
							<div className="muted center" style={{ padding: 24 }}>
								Aucune donnée sur cette période.
							</div>
						) : (
							<AdminBarChart
								data={Array.from({ length: 24 }, (_, h) => {
									const key = String(h).padStart(2, "0");
									return hourRows.find((r) => r.value === key)?.humans ?? 0;
								})}
								labels={Array.from({ length: 24 }, (_, h) =>
									h % 3 === 0 ? `${h}h` : "",
								)}
								color="var(--a-forest)"
								height={180}
							/>
						)}
					</CardBody>
				</Card>

				<Card>
					<CardHead title="Liens les plus ouverts" sub="Top 20 sur la période" />
					<CardBody>
						{(data?.topLinks ?? []).length === 0 ? (
							<div className="empty">Aucun clic enregistré sur cette période.</div>
						) : (
							<div className="table-wrap">
								<table className="table">
									<thead>
										<tr>
											<th>Code</th>
											<th>Libellé</th>
											<th className="right">Clics humains</th>
											<th className="right">Uniques</th>
										</tr>
									</thead>
									<tbody>
										{(data?.topLinks ?? []).map((l) => (
											<tr
												key={l.id}
												style={{ cursor: "pointer" }}
												onClick={() => navigate(`/liens-courts/${l.id}`)}
											>
												<td className="mono">{l.code}</td>
												<td>{l.label ?? <span className="muted">—</span>}</td>
												<td className="right tn">{fmtN(l.humans)}</td>
												<td className="right tn">{fmtN(l.uniques)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</CardBody>
				</Card>
			</div>
		</>
	);
}
